import { useMemo, useState, useEffect } from 'react'
import type { FC } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { Link, useLocation } from 'react-router-dom'
import type { RouteComponentProps } from 'react-router-dom'
import {
	Container,
	Heading,
	Text,
	Box,
	Skeleton,
	SkeletonText,
	Image,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	Button,
	Tag,
	TagLabel,
	Flex,
	Link as ChakraLink,
	ChakraProps,
	useDisclosure,
} from '@chakra-ui/react'
import { Marker } from 'react-map-gl'
import {
	ContentSection,
	ContentSectionHeading,
	SingleLayout,
	SingleHeader,
	SingleHero,
	SingleBody,
	PinIcn,
	Banner,
	BannerAvatar,
	BannerHeading,
	BannerSubHeading,
	IcnClock,
	InfoCircle,
} from '@alexanderathoodly/ui-library'
import {
	SIZE_1070,
	SIZE_410,
	getImgProxyUrl,
	SIZE_1200,
	SIZE_290,
	PLACEHOLDER_SRC,
} from '@yta/image-proxy'
import { PublicPageLayout, ErrorPage } from '~/layouts/pages'
import { getLocationByUrlQuery } from '~/queries/coworking_location'
import { OfficesDisplay, OfficeSwitcher, CurrentOfficeDisplay } from './offices'
import { TestimonialSection } from './testimonials'
import { LocationPageContext, CurrentOfficeContext } from './context'
import Map from './map'
import { usePersonOfTheDay } from '~/forms/utils'
import { Breadcrumb } from './breadcrumb'
import { GAAction, sendGA4Event } from '~/analytics/ga'
import { useOfficeSearchParams } from './utils'
import type {
	StatusError,
	FlexOfficeResult,
} from '@alexanderathoodly/data-models'
import {
	CountryCode,
	flexOfficesByLocation,
	useYtaQuery,
	useAuthQuery,
} from '@alexanderathoodly/data-models'
import {
	getFirstBookableDayAndTime,
	RequestShowingButton,
} from '~/forms/showingrequestform'
import { SingleLocationAside, SingleLocationAsideItem } from './aside'
import NotFoundPageLayout from '../notfound/layout'
import { ImgProxy } from '~/utils/imgproxy'
import ProviderSection from './provider'
import SimilarLocationsSection from './similarlocations'
import { DEFAULT_PHONE_NUMBER, PHONE_NUMBER_LINK } from '~/settings'
import { FlexOfficeLocationHelmet } from './helmet'
import { getProviderQuery } from './queries'
import { Temporal } from '@js-temporal/polyfill'
import { useTranslation } from 'react-i18next'
import { useAlternateLinks } from '~/utils/alternatelinks'
import { useUser } from 'reactfire'
import {
	ShowPriceButton,
	ShowPriceModal,
	useShowPriceModal,
} from '~/forms/showpriceform'
import { useDefaultOffice } from './utils'

const FlexOfficeInfoBox: FC<ChakraProps> = ({ ...rest }) => {
	const { t } = useTranslation()
	return (
		<Box
			bg="white"
			borderRadius="base"
			height="min-content"
			border="1px"
			borderColor="grey.100"
			shadow="xl"
			py={7}
			px={6}
			{...rest}
		>
			<Flex alignItems="center" mb={3}>
				<InfoCircle h={4} w={4} />
				<Heading as="h3" fontSize="md" fontWeight="semibold" ml={3}>
					{t('Eget kontor - en nyhet på yta.se. Vad innebär det?')}
				</Heading>
			</Flex>
			<Text>
				{t(
					'Egna kontor med kortare avtal samt möjlighet till möbler, drift och service - ett mellanting mellan kontorshotell och traditionella kontor med långa avtal. Kontoret delas ej med andra företag och utbudet av  service varierar per hyresvärd.'
				)}
			</Text>
		</Box>
	)
}

const FlexOfficeLocationLayout: FC<
	RouteComponentProps<{ url: string; office?: string }>
> = ({ match }) => {
	const { t, i18n } = useTranslation()
	const alternateLinks = useAlternateLinks('/kontor', match.params.url)
	const params = useOfficeSearchParams()
	const { search } = useLocation()
	const locationResult = useQuery(
		getLocationByUrlQuery(match.params.url, useQueryClient())
	)
	const providerResult = useQuery(
		getProviderQuery(locationResult.data?.provider)
	)

	const { status: userStatus, data: user } = useUser()
	const { isShowPriceModalOpen, onShowPriceModalClose, onShowPriceModalOpen } =
		useShowPriceModal('conversion-box', locationResult.data?.id || '')
	const todaysExpert = usePersonOfTheDay()
	const {
		isOpen: isImagesModalOpen,
		onOpen: onImagesModalOpen,
		onClose: onImagesModalClose,
	} = useDisclosure()

	const resultWithoutAuth = useYtaQuery(
		flexOfficesByLocation({
			location: locationResult.data?.id || '',
		})
	)
	const resultWithAuth = useAuthQuery(
		flexOfficesByLocation({
			location: locationResult.data?.id || '',
		})
	)
	const officesResult = user ? resultWithAuth : resultWithoutAuth
	const { data: offices } = officesResult
	const defaultOffice = useDefaultOffice(match.params.office, offices)
	const [currentOffice, setCurrentOffice] = useState<
		FlexOfficeResult | undefined
	>(defaultOffice)

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [currentOffice])

	useEffect(() => {
		if (defaultOffice !== currentOffice) {
			setCurrentOffice(defaultOffice)
		}
	}, [defaultOffice, setCurrentOffice, currentOffice])
	const heroImg = useMemo(() => {
		if (
			offices &&
			currentOffice &&
			currentOffice.images &&
			currentOffice.images[0]
		) {
			return currentOffice.images[0]
		}
		return PLACEHOLDER_SRC
	}, [offices, currentOffice])

	const imageElems = useMemo(() => {
		if (currentOffice == undefined || locationResult.data == undefined) {
			return {}
		}

		const images = currentOffice.images.map((src) => (
			<ImgProxy
				key={src}
				src={src}
				sizeSet={{ base: SIZE_290, md: SIZE_410, lg: SIZE_1070 }}
				w="100%"
				alt={
					locationResult.data?.business_area
						? `kontor i ${
								locationResult.data.business_area.split('/')[0] || ''
						  } - ${locationResult.data.name}`
						: `kontor`
				}
			/>
		))
		const locationImages = locationResult.data.images.map((src) => (
			<ImgProxy
				key={src}
				src={src}
				sizeSet={{ base: SIZE_290, md: SIZE_410, lg: SIZE_1070 }}
				w="100%"
				alt={
					locationResult.data?.business_area
						? `kontor i ${
								locationResult.data.business_area.split('/')[0] || ''
						  } - ${locationResult.data.name}`
						: `kontor`
				}
			/>
		))
		return { images, locationImages }
	}, [locationResult.data, currentOffice])
	if (
		locationResult.isLoading ||
		locationResult.isIdle ||
		officesResult.isLoading ||
		officesResult.isIdle
	) {
		return (
			<PublicPageLayout>
				<Container maxW="container.lg" minH={2000}>
					<SingleLayout>
						<SingleHeader>
							<Skeleton h={10} />
						</SingleHeader>
						<SingleHero position="relative">
							<Skeleton h={52} />
						</SingleHero>
						<SingleBody>
							<SkeletonText noOfLines={6} spacing={4} mb={14} />
							<SkeletonText noOfLines={6} spacing={4} />
						</SingleBody>
					</SingleLayout>
				</Container>
			</PublicPageLayout>
		)
	}
	if (locationResult.isError) {
		if ((locationResult.error as StatusError).statusCode == 404) {
			return <NotFoundPageLayout />
		}
		return <ErrorPage error={locationResult.error} />
	}
	const location = locationResult.data
	const today = Temporal.Now.plainDateTime('iso8601', 'Europe/Stockholm')
	const firstBookableDay = getFirstBookableDayAndTime(today, location)
	return (
		<PublicPageLayout>
			<LocationPageContext.Provider value={{ location }}>
				<CurrentOfficeContext.Provider value={{ currentOffice: defaultOffice }}>
					<FlexOfficeLocationHelmet location={location}>
						<title>{`${t('FlexOfficeLocationPageMetaTitle', {
							ns: 'meta_data',
							location_name: `${providerResult.data?.name || ''} ${
								location.address.street
							} ${location.address.locator}`,
						})}`}</title>
						<meta
							name="description"
							content={
								i18n.language != 'en'
									? location.short_description
									: location.short_description_en
							}
						/>
						<meta
							name="robots"
							content={
								search ||
								match.params.office ||
								!location.is_flex_office ||
								!location.advertisable ||
								location.address.country == CountryCode.DENMARK
									? 'noindex, follow'
									: 'index, follow'
							}
						/>
						{location.images[0] && (
							<meta
								property="og:image"
								content={getImgProxyUrl(
									currentOffice?.images[0] || location.images[0],
									SIZE_1200
								)}
							/>
						)}
						<meta
							property="og:image:width"
							content={SIZE_1200.width.toString()}
						/>
						<meta
							property="og:image:height"
							content={SIZE_1200.height.toString()}
						/>
						{alternateLinks &&
							alternateLinks.map((link) => (
								<link
									key={link.url}
									rel="alternate"
									hrefLang={link.lng}
									href={link.url}
								/>
							))}
						{alternateLinks && (
							<link
								rel="alternate"
								hrefLang="x-default"
								href={alternateLinks.find((link) => link.lng == 'sv')!.url}
							/>
						)}
						{alternateLinks && (
							<link
								rel="canonical"
								href={
									alternateLinks.find((link) => link.lng == i18n.language)!.url
								}
							/>
						)}
					</FlexOfficeLocationHelmet>
					<Container maxW="container.lg" minH={2000}>
						<SingleLayout
							gridTemplateRows="min-content 5.5rem 5.5rem 1fr"
							gridTemplateColumns={{ base: '1fr', lg: '66% 1fr' }}
						>
							<SingleHeader>
								<Heading as="h1">{location.name}</Heading>
								<Text color="grey.500" mt={1}>
									{t('Se alla lediga flexibla kontor på {{location_name}}.', {
										location_name: location.name,
									})}
								</Text>
								<Flex
									flexDirection={{ base: 'column', md: 'row' }}
									justifyContent="space-between"
									gap="0.5rem"
								>
									<Breadcrumb location={location} />
									<Box>
										{location.highlights.map((highlight) => (
											<Tag
												key={highlight}
												backgroundColor="blue.50"
												borderRadius="full"
												ml={1}
											>
												<TagLabel my={2}>{t(highlight)}</TagLabel>
											</Tag>
										))}
									</Box>
								</Flex>
							</SingleHeader>
							<SingleHero position="relative" height="fit-content">
								<ImgProxy
									src={heroImg}
									sizeSet={{ base: SIZE_290, md: SIZE_410, lg: SIZE_1070 }}
									w="100%"
									css={{ aspectRatio: '18 / 10' }}
									alt={t('Flexkontor i {{city}} - {{location_name}}', {
										ns: 'meta_data',
										city: location.address.city,
										location_name: location.name,
									})}
								/>
								<Tag
									borderRadius="full"
									height="max-content"
									alignSelf="center"
									py={3}
									px={5}
									position="absolute"
									top={4}
									right={4}
									backgroundColor="#EBC22A"
								>
									{t('Eget kontor')}
								</Tag>
								<Box bottom={4} left={4} position="absolute">
									<Button
										onClick={onImagesModalOpen}
										variant="outline"
										bg="white"
										mr={2}
									>
										{t('Alla bilder på kontoret')}
									</Button>
									<Button
										variant="outline"
										bg="white"
										display={{
											base: 'none',
											sm:
												currentOffice?.floorplans &&
												currentOffice.floorplans.length > 0
													? 'inline-flex'
													: 'none',
										}}
										onClick={() => {
											// Using timeout just to give the image element to be in the dom once the modal opens,
											// 'getElementById' was smoother than having ref's for all images.
											setTimeout(() => {
												const element = document.getElementById(
													'floorplans-and-conditions'
												)
												if (!element) {
													return
												}
												element.scrollIntoView({
													behavior: 'smooth',
													block: 'center',
												})
											}, 300)
										}}
									>
										{t('Planritningar')}
									</Button>
								</Box>
								<OfficeSwitcher
									bottom={4}
									right={4}
									position="absolute"
									display={{ base: 'none', md: 'block' }}
								/>
							</SingleHero>

							<SingleLocationAside
								display={{ base: 'none', lg: 'block' }}
								bottom="-100px"
							>
								<SingleLocationAsideItem>
									<Box
										display={{ base: 'none', lg: 'grid' }}
										gridTemplate={{
											base: 'auto auto / auto auto',
											lg: 'repeat(3, auto) / 1fr',
										}}
									>
										<Heading
											mb={2}
											fontWeight="semibold"
											color="grey.800"
											fontSize="2xl"
										>
											{t('Intresserad av kontoret?')}
										</Heading>
										<Text mb={5} color="blackAlpha.700" fontSize="md">
											{t(
												'Gå på en rundvandring av kontoret - önska dag och tid i nästa steg.'
											)}
										</Text>
										<Flex
											alignItems="center"
											gap={3}
											h={10}
											bgColor="blue.50"
											mb={4}
											p={2}
										>
											<IcnClock
												width={5}
												height={5}
												display={{ base: 'none', md: 'inline-block' }}
											/>
											<Text
												color="blackAlpha.700"
												fontSize="xs"
												fontStyle="italic"
											>
												{`${t('Första möjliga tid')}: ${
													Temporal.PlainDate.compare(
														firstBookableDay,
														today
													) === 0
														? `${t('Idag')} ${firstBookableDay
																.round({
																	roundingMode: 'floor',
																	smallestUnit: 'hour',
																})
																.toLocaleString(`${i18n.language}-SE`, {
																	hour: 'numeric',
																	minute: 'numeric',
																})}`
														: firstBookableDay
																.round({
																	roundingMode: 'floor',
																	smallestUnit: 'hour',
																})
																.toLocaleString(`${i18n.language}-SE`, {
																	weekday: 'long',
																	hour: 'numeric',
																	minute: 'numeric',
																})
												}`}
											</Text>
										</Flex>
										<RequestShowingButton
											location={location}
											workspaces={params.workspaces}
											mb={3}
										/>
										{userStatus == 'loading' ? (
											<Skeleton w="100%" h={10} borderRadius="md" />
										) : (
											!user && (
												<ShowPriceButton
													GA4EventLocation="conversion box"
													onClick={onShowPriceModalOpen}
												>
													{t('Begär offert')}
												</ShowPriceButton>
											)
										)}
									</Box>
								</SingleLocationAsideItem>
								<Text mx={3} textAlign="center" color="grey.500" fontSize="md">
									{t('Har du frågor om kontoret? Ring oss på')}{' '}
									<ChakraLink
										color="blue.500"
										fontWeight="semibold"
										href={PHONE_NUMBER_LINK}
										onClick={() =>
											sendGA4Event('click_phone_number', {
												operator:
													providerResult.data?.name ||
													'failed_to_get_operator_name', // We don't want to affect the app even if fetching provider for GA fails
												location: location.name,
											})
										}
									>
										{DEFAULT_PHONE_NUMBER}
									</ChakraLink>
									.
								</Text>
							</SingleLocationAside>
							<SingleBody>
								<Modal isOpen={isImagesModalOpen} onClose={onImagesModalClose}>
									<ModalOverlay />
									<ModalContent maxW="6xl">
										<ModalHeader>
											{currentOffice?.external_id || t('Bilder')}
										</ModalHeader>
										<ModalCloseButton />
										<ModalBody display="flex" flexDirection="column" gap="50px">
											{imageElems.images?.concat(imageElems.locationImages)}
										</ModalBody>
									</ModalContent>
								</Modal>
								<ContentSection
									display={{ base: 'flex', md: 'none' }}
									gap={3}
									borderTop="none"
									pt={1}
								>
									<Button
										variant="outline"
										bg="white"
										display={{
											base:
												currentOffice?.floorplans &&
												currentOffice.floorplans.length > 0
													? 'inline-flex'
													: 'none',
											sm: 'none',
										}}
										onClick={() => {
											// Using timeout just to give the image element to be in the dom once the modal opens,
											// 'getElementById' was smoother than having ref's for all images.
											setTimeout(() => {
												const element = document.getElementById(
													'floorplans-and-conditions'
												)
												if (!element) {
													return
												}
												element.scrollIntoView({
													behavior: 'smooth',
													block: 'center',
												})
											}, 300)
										}}
									>
										{t('Planritningar')}
									</Button>
									<OfficeSwitcher display={{ md: 'none' }} />
								</ContentSection>
								<ContentSection
									display="flex"
									gap={6}
									flexWrap="wrap"
									borderTop="none"
									pt={0}
								>
									<Box>
										<Text
											fontWeight="semibold"
											fontSize={{ base: 'xl', md: '2xl' }}
										>
											{t('Eget kontor')}
										</Text>
										<Text fontSize="medium" color="grey.500">
											{t('Kontorstyp')}
										</Text>
									</Box>
									{offices && offices?.length > 0 && (
										<Box>
											<Text
												fontWeight="semibold"
												fontSize={{ base: 'xl', md: '2xl' }}
											>
												{offices.length}
											</Text>
											<Text fontSize="medium" color="grey.500">
												{t('Lediga kontor')}
											</Text>
										</Box>
									)}
									<Tag
										borderRadius="full"
										height="max-content"
										alignSelf="center"
										py={3}
										px={5}
									>
										{t('Flexibla avtal')}
									</Tag>
									<Tag
										borderRadius="full"
										height="max-content"
										alignSelf="center"
										py={3}
										px={5}
									>
										{t('Full service')}
									</Tag>
									{providerResult.data && (
										<ChakraLink
											href={`${t('/operatörer', { ns: 'url' })}/${
												location.provider
											}`}
										>
											<ImgProxy
												src={providerResult.data.logo}
												sizeSet={{ base: SIZE_290 }}
												maxH="6rem"
												maxW="6rem"
											/>
										</ChakraLink>
									)}
								</ContentSection>
								<FlexOfficeInfoBox mb="1rem" />
								<CurrentOfficeDisplay />
								{offices && offices?.length > 1 && (
									<ContentSection pb={2}>
										<ContentSectionHeading>
											{t('Andra tillgängliga kontor på {{location_name}}', {
												location_name: location.name,
											})}
										</ContentSectionHeading>
										<Text pb={3}>
											{t(
												'Alla kontor som är tillgängliga på {{location_name}}. Välj ett av alternativen för att se fler bilder och mer information om avtalslängd, service och innehåll.',
												{
													location_name: location.name,
												}
											)}
										</Text>
										<OfficesDisplay />
									</ContentSection>
								)}
								<ContentSection>
									<ContentSectionHeading>
										{`${t('Om')} ${
											location.name
										}${`, ${location.breadcrumbs.area.name}`}`}
									</ContentSectionHeading>
									<Text whiteSpace="pre-wrap" color="grey.500">
										{i18n.language != 'en'
											? location.description
											: location.description_en}
									</Text>
								</ContentSection>
								<ProviderSection url={match.params.url} />
								<TestimonialSection />
								<ContentSection>
									<ContentSectionHeading>{t('Området')}</ContentSectionHeading>
									<Map
										latitude={location.address.coordinates.lat}
										longitude={location.address.coordinates.lng}
										css={{ contentVisibility: 'auto' }}
									>
										<Marker
											latitude={location.address.coordinates.lat}
											longitude={location.address.coordinates.lng}
										>
											<Box as="button">
												<PinIcn
													height={10}
													width={7}
													outer={{
														fill: 'blue.500',
														stroke: 'blue.500',
													}}
													inner={{ fill: 'white' }}
												/>
											</Box>
										</Marker>
									</Map>
								</ContentSection>
								<ContentSection>
									<Box as={Link} to={t('/kontorshjalpen', { ns: 'url' })}>
										<Banner
											variant="horizontal"
											size="lg"
											onClick={() => {
												GAAction({
													category: 'engagement',
													action: 'click_expert_banner',
													label: 'expert',
												})
												sendGA4Event('click_kontorshjalpen', {
													page_type: 'flex_office_location',
													placement: 'content',
													placement_type: 'banner',
													content: 'expert',
												})
											}}
										>
											<BannerAvatar>
												<Image
													src={todaysExpert.img}
													borderRadius="full"
													minWidth="80px"
													alt={todaysExpert.name}
												/>
											</BannerAvatar>
											<BannerHeading>
												{t(
													'Behöver du hjälp att hitta rätt lösning? Boka ett gratis rådgivningsmöte.'
												)}
											</BannerHeading>
											<BannerSubHeading>
												<strong>{todaysExpert.name}</strong>,{' '}
												{t(todaysExpert.positionTitle)}. 100%{' '}
												{t('kostnadsfritt')}
											</BannerSubHeading>
										</Banner>
									</Box>
								</ContentSection>
							</SingleBody>
						</SingleLayout>
						<SimilarLocationsSection url={match.params.url} />
					</Container>
					<SingleLocationAside
						display={{ lg: 'none' }}
						px={3}
						py={3}
						w="100%"
						borderTop="1px"
						borderColor="grey.100"
						background="white"
						zIndex={2}
					>
						<Text mb={3} fontWeight="semibold">
							{t('Intresserad av det här kontoret?')}
						</Text>
						<Flex>
							{userStatus == 'loading' ? (
								<Skeleton w="100%" h={10} borderRadius="md" />
							) : (
								!user && (
									<ShowPriceButton
										GA4EventLocation="conversion box"
										onClick={onShowPriceModalOpen}
										mr={5}
										width="100%"
									>
										{t('Begär offert')}
									</ShowPriceButton>
								)
							)}
							<RequestShowingButton
								location={location}
								workspaces={params.workspaces}
							/>
						</Flex>
					</SingleLocationAside>
					<ShowPriceModal
						location={location}
						isOpen={isShowPriceModalOpen}
						onClose={onShowPriceModalClose}
					/>
				</CurrentOfficeContext.Provider>
			</LocationPageContext.Provider>
		</PublicPageLayout>
	)
}

export default FlexOfficeLocationLayout
